import { toast } from 'react-toastify';

// One entry point for every user-facing message in the app.
// Replaces the scattered alert() calls, and makes sure a single action can
// never stack three identical toasts on top of each other.

const DEFAULT_OPTIONS = {
    position: "bottom-left",
    autoClose: 4500,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    role: "status"
};

export function currentLanguage() {
    const lang = localStorage.language;
    return (!lang || lang === "null") ? "geo" : lang;
}

/**
 * The API answers with {en, geo, ru, am} objects. Turns one of those (or a
 * plain string) into text for the active language.
 */
export function localize(message, fallback = "") {
    if (typeof message === "string") return message;
    if (message && typeof message === "object") {
        const lang = currentLanguage();
        return message[lang] || message.geo || message.en || fallback;
    }
    return fallback;
}

// A stable id per message text, so re-submitting the same failing form
// refreshes the existing toast rather than adding another one.
function toastId(prefix, text) {
    return `${prefix}:${String(text).slice(0, 80)}`;
}

function show(type, message, options = {}) {
    const text = localize(message);
    if (!text) return null;
    return toast[type](text, {...DEFAULT_OPTIONS, toastId: toastId(type, text), ...options});
}

/**
 * Pulls a human message out of an axios error. Technical details (stack traces,
 * SQL errors, raw 500 bodies) never reach the user — they go to the console.
 */
export function apiErrorMessage(error, fallback) {
    const response = error && error.response;

    if (!response) {
        // Network failure / server unreachable / request cancelled.
        return localize({
            en: "Connection problem. Please check your internet and try again.",
            geo: "კავშირის პრობლემა. შეამოწმეთ ინტერნეტი და სცადეთ თავიდან.",
            ru: "Проблема соединения. Проверьте интернет и попробуйте снова.",
            am: "Կապի խնդիր։ Ստուգեք ինտերնետը և կրկին փորձեք։"
        });
    }

    const data = response.data || {};

    // A validation error is specific enough to show as-is.
    if (data.field_errors && typeof data.field_errors === "object") {
        const first = Object.values(data.field_errors)[0];
        const text = localize(first);
        if (text) return text;
    }

    const text = localize(data.message);
    if (text) return text;

    if (response.status === 403) {
        return localize({
            en: "You do not have permission for this action.",
            geo: "თქვენ არ გაქვთ ამ მოქმედების უფლება.",
            ru: "У вас нет прав на это действие.",
            am: "Դուք չունեք այս գործողության թույլտվություն։"
        });
    }

    if (response.status === 404) {
        return localize({
            en: "Not found.", geo: "ვერ მოიძებნა.", ru: "Не найдено.", am: "Չի գտնվել։"
        });
    }

    return localize(fallback) || localize({
        en: "Something went wrong. Please try again.",
        geo: "დაფიქსირდა შეცდომა. გთხოვთ სცადოთ თავიდან.",
        ru: "Что-то пошло не так. Попробуйте ещё раз.",
        am: "Ինչ-որ բան սխալ գնաց։ Կրկին փորձեք։"
    });
}

/** Field-level errors, already localized, keyed by field name. */
export function apiFieldErrors(error) {
    const raw = error && error.response && error.response.data && error.response.data.field_errors;
    if (!raw || typeof raw !== "object") return {};
    return Object.entries(raw).reduce((acc, [field, message]) => {
        acc[field] = localize(message);
        return acc;
    }, {});
}

const notify = {
    success: (message, options) => show('success', message, options),
    error:   (message, options) => show('error', message, options),
    warning: (message, options) => show('warning', message, options),
    info:    (message, options) => show('info', message, options),

    /** Logs the technical detail, shows the user a message they can act on. */
    apiError(error, fallback, options) {
        if (error) console.error(error);
        return show('error', apiErrorMessage(error, fallback), options);
    },

    dismiss: (id) => toast.dismiss(id)
};

export default notify;
